/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ISizerCellComponent, ISizerFilterSize } from 'app/services/sizerApi';

/**
 * Phase 4b — the `/sizer/` `localStorage` contracts.
 *
 * ★★ BYTE-COMPATIBILITY WITH LEGACY. While the port was in progress the legacy
 * Backbone sizer was still live, and the header's `sizer_popup` view
 * (`django/static/mvc/header/view/sizer_popup.js`) read two of these keys on
 * EVERY page. Every page that carried that header is React now and no
 * Backbone reader remains, but the stored format is kept byte-identical to what
 * `fn.local_storage.set` wrote: values already in users' browsers must still
 * parse, the React readers below rely on the same shapes, and `sizer_settings`
 * has readers outside this file — the SPA KAI Select port
 * (`pages/kai-select/helpers/bot/designAHU.sizer.ts`,
 * `.../designAHU/utils/resolveDefaults.utils.ts`), which `JSON.parse` it
 * directly. (The legacy S3 KAI Select widget at `/app/kai-select/`, also a
 * reader, was retired 2026-10-11.)
 *
 * `fn.local_storage` (django/static/js_scripts/local_storage.js:9-19):
 *   set(k, v) → `typeof v == "object" ? JSON.stringify(v) : v` then setItem
 *               (so a NUMBER is stored as its bare decimal string, NOT `"5"`).
 *   get(k)    → `raw.includes("{") ? JSON.parse(raw) : raw`
 *               (⚠ the `{` sniff, not a try/parse: a JSON ARRAY without any
 *                object inside — e.g. `[1,2]` — comes back as the raw STRING.
 *                None of the three sizer keys can hit that: `sizer_settings`
 *                and `sizer_popup` are objects, `d-sizer-airflow` is a scalar.)
 *
 * THE THREE CONTRACTS (discovery §4a)
 *
 * 1. `sizer_settings` — the settings-panel cache. One entry per `.cached`
 *    control `name`, plus `{name}_id` from the selected option's `data-id` and
 *    `{name}_value` from its `data-value` (view/main.js:462-463), plus
 *    `allowable_filter_sizes` (the FULL filter-size row objects, :801).
 *    Written by `updateFieldsCache` (:467-476),
 *    `getDataDependsOnConstructionClass` (:406) and the `initialize`
 *    re-write-on-load (:98, :106).
 *    Read at :65 (default `{construction_class_id: 1}` when ABSENT — and a
 *    fresh browser genuinely has no key, OBSERVED), at :206-210 (Max-FH
 *    restore) and at :301-325 (restore every `.cached` control).
 *    OBSERVED after changing two fields:
 *      {"construction_class_id":1,"cc_face_vel_max":"450","cc_tube":"4",
 *       "cc_tube_value":0.625}
 *    ⚠ VALUE TYPES ARE MIXED ON PURPOSE — `construction_class_id` is the
 *    NUMBER 1 until the user changes Product Class, after which it is the
 *    select's STRING value; control values are the DOM strings; `_id`/`_value`
 *    are coerced with unary `+` so they are numbers. Reproduced exactly.
 *
 * 2. `d-sizer-airflow` — the airflow the user last clicked a cell with.
 *    `localStorage.setItem('d-sizer-airflow', +airflow)` (main.js:632) → the
 *    bare decimal string of a NUMBER (so `20000`, never `"20,000"`), removed
 *    when the airflow field is empty (:633). Read by the header popup's
 *    `apply()` (in legacy sizer_popup.js:865, :903; now
 *    `selection/sizerSelectionController.ts`) — which is the ONLY reason the
 *    write path exists, because `selectionData.airflow` is always `undefined`
 *    (main.js:623 reads `.value` off a jQuery object). Writer lands with the
 *    cell-click (4d/4e); the contract lives here.
 *
 * 3. `sizer_popup` — the clicked cell's `selectionData`, `fn.local_storage.set`
 *    so `JSON.stringify` of an object (main.js:634). Read by the header
 *    popup's controller (`selection/sizerSelectionController.ts`) — its
 *    cross-tab `storage` listener and restore-on-load (in legacy:
 *    header.js:213-224 and `sizer_popup.js`). Writer lands with 4d/4e.
 *
 * NOT OURS: `sizer_popup_state`, `sizer_popup_fields_status`,
 * `sizer_doesnt_need_update` are written exclusively by the header popup
 * (discovery §4a; in React its controller, via
 * `selection/sizerPopupStorage.ts`) — the sizer page never touches them.
 */

export const SIZER_SETTINGS_KEY = 'sizer_settings';
export const SIZER_AIRFLOW_KEY = 'd-sizer-airflow';
export const SIZER_POPUP_KEY = 'sizer_popup';

/**
 * The `sizer_settings` bag. Deliberately loose: legacy writes whatever control
 * names exist, so an unknown key must round-trip untouched (forward/backward
 * compatibility is the stated design goal — main.js:295-299).
 */
export interface SizerSettingsCache {
  /** number `1` from the default object; a STRING once Product Class changes. */
  construction_class_id?: number | string;
  /** the committed multiselect — FULL rows, not ids (main.js:801). */
  allowable_filter_sizes?: ISizerFilterSize[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// fn.local_storage primitives
// ---------------------------------------------------------------------------

function safeGetItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota / private mode — legacy would throw; nothing here depends on it */
  }
}

function safeRemoveItem(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * `fn.local_storage.get` for an OBJECT key — the `includes("{")` sniff verbatim,
 * with the one deliberate deviation that a malformed value yields `null`
 * instead of an uncaught `SyntaxError` (legacy would take down the whole view;
 * that state is only reachable via hand-edited storage).
 *
 * ★ 4e re-exports these three primitives (`readObject` / `writeObject` /
 * `safeRemoveItem`, as `readLocalStorageObject` / `writeLocalStorageObject` /
 * `removeLocalStorageItem`) so the selection popup's own four keys
 * (`sizer_popup_state`, `sizer_popup_fields_status`, `sizer_doesnt_need_update`,
 * `draggable-popup` — see `selection/sizerPopupStorage.ts`) go through ONE
 * implementation of the `fn.local_storage` contract instead of a second copy.
 */
function readObject<T>(key: string): T | null {
  const raw = safeGetItem(key);
  if (!raw || !raw.includes('{')) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as T) : null;
  } catch {
    return null;
  }
}

/** `fn.local_storage.set(key, object)` — `JSON.stringify`, no spacing. */
function writeObject(key: string, value: unknown): void {
  safeSetItem(key, JSON.stringify(value));
}

// ---- shared with `selection/sizerPopupStorage.ts` (4e) ---------------------
export const readLocalStorageObject = readObject;
export const writeLocalStorageObject = writeObject;
export const readLocalStorageRaw = safeGetItem;
export const writeLocalStorageRaw = safeSetItem;
export const removeLocalStorageItem = safeRemoveItem;

// ---------------------------------------------------------------------------
// 1. sizer_settings
// ---------------------------------------------------------------------------

/**
 * `fn.local_storage.get('sizer_settings') || {construction_class_id: 1}`
 * (main.js:65). The fallback is NOT persisted — a fresh browser leaves the key
 * absent until the first real `updateFieldsCache`, which is what the discovery
 * OBSERVED. Callers therefore get the default object but must not write it.
 */
export function readSizerSettings(): SizerSettingsCache {
  return readObject<SizerSettingsCache>(SIZER_SETTINGS_KEY) ?? { construction_class_id: 1 };
}

/** True when the key genuinely exists (used to keep the no-write-on-load rule
 * honest and to mirror `initialize`'s conditional re-write at main.js:96-99). */
export function hasSizerSettings(): boolean {
  const raw = safeGetItem(SIZER_SETTINGS_KEY);
  return !!raw && raw.includes('{');
}

/**
 * `updateFieldsCache` (main.js:467-476): merge the overrides into the in-memory
 * bag and persist the WHOLE bag. Last writer wins across tabs — the legacy
 * comment at :468-470 calls that out as intended.
 */
export function writeSizerSettings(settings: SizerSettingsCache): void {
  writeObject(SIZER_SETTINGS_KEY, settings);
}

// ---------------------------------------------------------------------------
// 2. d-sizer-airflow
// ---------------------------------------------------------------------------

/** Raw scalar read (`fn.local_storage.get` returns the raw string for scalars). */
export function readSizerAirflow(): string | null {
  return safeGetItem(SIZER_AIRFLOW_KEY);
}

/**
 * main.js:632-633 verbatim: a truthy airflow STRING (already comma-stripped by
 * the caller) is stored as `String(+airflow)`; anything falsy REMOVES the key.
 * `+airflow` of a non-numeric string is `NaN`, which stores literally as
 * `"NaN"` — legacy behaviour, kept.
 */
export function writeSizerAirflow(airflow: string): void {
  if (airflow) safeSetItem(SIZER_AIRFLOW_KEY, String(Number(airflow)));
  else safeRemoveItem(SIZER_AIRFLOW_KEY);
}

// ---------------------------------------------------------------------------
// 3. sizer_popup
// ---------------------------------------------------------------------------

/** The header popup's payload — shape OBSERVED in discovery §7b. */
export interface SizerPopupSelection {
  current_width?: string;
  current_height?: string;
  /** the matrix cell's own component readouts, spread in verbatim
   * (main.js:613-615) — `ISizerCellComponent` as `sizerApi.ts` declares it. */
  filter?: ISizerCellComponent;
  cooling_coil?: ISizerCellComponent;
  heating_coil?: ISizerCellComponent;
  fan?: Record<string, unknown>;
  casing_insulation_id?: number;
  /** a STRING — it comes straight off the select (main.js:619). */
  construction_class_id?: string;
  /** ⚠ ALWAYS ABSENT in practice: main.js:623 reads `.value` off a jQuery
   * object, so the key is `undefined` and `JSON.stringify` drops it. The
   * header falls back to `d-sizer-airflow`. Reproduce the ABSENCE. */
  airflow?: undefined;
  [key: string]: unknown;
}

export function readSizerPopupSelection(): SizerPopupSelection | null {
  return readObject<SizerPopupSelection>(SIZER_POPUP_KEY);
}

export function writeSizerPopupSelection(selection: SizerPopupSelection): void {
  writeObject(SIZER_POPUP_KEY, selection);
}
